import { Tooltip } from 'antd';
import AppButton from '@nxsys/components/AppButton';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultCisStatus,
  defaultPaymentFrequency,
  defaultRegistrationStatus,
} from '@nxsys/constants/AppConstants';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetEmployeeDashboardListParams } from '@nxsys/store/slices/dashboard/types';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { defaultMarginMethod, formatStatus } from '@nxsys/utils';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce, filter, find } from 'lodash';
import moment from 'moment';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export enum EmployeeModalTypeEnum {
  TOTAL = 'TOTAL',
  PENDING_REGISTRATION = 'PENDING_REGISTRATION',
  ON_HOLD = 'ON_HOLD',
  PENSION_OPT_OUT = 'PENSION_OPT_OUT',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: EmployeeModalTypeEnum;
};

const EmployeeModal: React.FC<Props> = ({ isOpen, toggle, type }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { employeeList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = employeeList;

  const [selectedRows, setSelectedRows] = useState<EmployeeRecordType[]>([]);

  const [selectedCompanies, setSelectedCompanies] = useState<ICompany[] | null>(
    null,
  );
  const [selectedAgencies, setSelectedAgencies] = useState<IAgency[] | null>(
    null,
  );

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
        companyIds: selectedCompanies?.map((item: any) => item.id) ?? undefined,
        agencyIds: selectedAgencies?.map((item: any) => item.id) ?? undefined,
      });
  }, [isOpen, selectedCompanies, selectedAgencies]);

  const handleFetchData = (params: GetEmployeeDashboardListParams) => {
    const newParams = {
      ...params,
    };
    if (type === EmployeeModalTypeEnum.PENDING_REGISTRATION) {
      newParams.isPendingOnly = true;
    } else if (type === EmployeeModalTypeEnum.ON_HOLD) {
      newParams.isOnHoldOnly = true;
    } else if (type === EmployeeModalTypeEnum.PENSION_OPT_OUT) {
      newParams.isPensionOptOutOnly = true;
    }
    dispatch(dashboardActions.getEmployeeDashboardList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const columns: ColumnsType<EmployeeRecordType> = [
    {
      key: 'actions',
      title: 'Action',
      fixed: 'left',
      width: 100,
      align: 'left',
      render: (record) => {
        return (
          <ul className="table-actions flex items-center justify-center">
            {!isBureau ? (
              <li>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center' }}>
                  <Tooltip title="View">
                    <span className="cursor-pointer" onClick={() => navigate(`/employees-subcontractors/${record.id}`)}>
                      <Icons.IconEye />
                    </span>
                  </Tooltip>
                  <Tooltip title="Add Timesheet">
                    <span className="cursor-pointer" onClick={() =>
                      navigate('/timesheets/add-new', {
                        query: {
                          from: '/employees-subcontractors',
                          defaultValues: JSON.stringify({
                            subcontractorRef: {
                              label:
                                record?.fullName ||
                                `${record.forename} ${record.surname}`,
                              value: record?.id,
                              fullData: record,
                            },
                          }),
                        },
                      })
                    }>
                      <Icons.IconFilePlus />
                    </span>
                  </Tooltip>
                  <Tooltip title="Add Assignment">
                    <span className="cursor-pointer" onClick={() =>
                      navigate(`/assignments/create`, {
                        query: {
                          from: location.pathname,
                          defaultValues: JSON.stringify({
                            employeeAndSubcontractor: {
                              label: `${record?.forename} ${record?.surname}`,
                              value: record.id,
                            },
                          }),
                        },
                      })
                    }>
                      <Icons.IconPlus />
                    </span>
                  </Tooltip>
                </div>
              </li>
            ) : (
              <li>
                <AppIconButton
                  title="View"
                  icon={<Icons.IconEye />}
                  onClick={() =>
                    navigate(`/employees-subcontractors/${record.id}`)
                  }
                />
              </li>
            )}
          </ul>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (status) => (
        <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
          {formatStatus(status)}
        </AppTagStatus>
      ),
      width: 140,
    },
    {
      key: 'id',
      title: 'Employee ID',
      dataIndex: 'id',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 140,
    },
    {
      key: 'title',
      title: 'Title',
      dataIndex: 'title',
      width: 120,
    },
    {
      key: 'name',
      title: 'Name',
      render: (props) => `${props?.forename || ''} ${props?.surname || ''}`.trim(),
      width: 220,
    },
    {
      key: 'agency_ref',
      title: 'Agency Ref',
      render: (record) =>
        `${record?.primaryAgency?.agencyPrefix || '___'}/${
          record?.agencyRef || ''
        }`,
      width: 240,
    },
    {
      key: 'holdReason',
      title: 'Reason',
      dataIndex: 'holdReason',
    },
    {
      key: 'companyPayment',
      title: 'Payment Company',
      dataIndex: 'companyPayment',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 300,
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'registrationStatus',
      title: 'Reg Status',
      dataIndex: 'registrationStatus',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultRegistrationStatus, (item) => item.value === value)
          ? filter(defaultRegistrationStatus, (item) => item.value === value)[0]
              ?.label
          : '',
      width: 240,
    },
    {
      key: 'payFrequency',
      title: 'Pay Frequency',
      dataIndex: 'payFrequency',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultPaymentFrequency, (item) => item?.value === value)
          ? filter(defaultPaymentFrequency, (item) => item?.value === value)[0]
              ?.label
          : '',
    },
    ...((!isBureau
      ? [
          {
            key: 'primaryAgency',
            title: 'Primary Agency',
            dataIndex: 'primaryAgency',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            width: 240,
            render: (value) => value?.name || '_',
          },
        ]
      : []) as ColumnsType<EmployeeRecordType>),
    {
      key: 'mobileNo',
      title: 'Mobile No',
      dataIndex: 'mobile',
      width: 180,
    },
    {
      key: 'emailID',
      title: 'Email ID',
      dataIndex: 'email',
      width: 240,
    },
    {
      key: 'NINo',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      dataIndex: 'NINumber',
      width: 180,
    },
    {
      key: 'UTRNo',
      title: 'UTR No',
      dataIndex: 'setting',
      render: (value) => value?.UTRNo || '_',
    },
    {
      key: 'CISStatus',
      title: 'CIS Status',
      dataIndex: 'setting',
      align: 'center',
      render: (value) =>
        value && value?.cisStatus
          ? filter(
              defaultCisStatus,
              (item) => item.value === value?.cisStatus,
            )[0].label
          : '_',
      width: 180,
    },
    {
      key: 'PVNo',
      title: 'PV No',
      dataIndex: 'PVNo',
      width: 180,
    },
    {
      key: 'Verification Date',
      title: 'Verification Date',
      dataIndex: 'setting',
      align: 'right',
      render: (value) =>
        value && value?.verificationDate
          ? moment(value?.verificationDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
      width: 240,
    },
    {
      key: 'Match Date',
      title: 'Match Date',
      dataIndex: 'setting',
      align: 'right',
      render: (value) =>
        value && value?.matchDate
          ? moment(value?.matchDate).tz('Europe/London').format('DD/MM/YYYY')
          : '_',
      width: 240,
    },
    {
      key: 'marginMethod',
      title: 'Margin Method',
      dataIndex: 'setting',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        value?.marginMethod &&
        filter(
          defaultMarginMethod,
          (item) => item.value === value?.marginMethod,
        )[0]
          ? filter(
              defaultMarginMethod,
              (item) => item.value === value?.marginMethod,
            )[0].label
          : '',
      width: 240,
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'setting',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.margin || '_',
    },
  ];

  const handleExport = () => {
    if (!selectedRows?.length) {
      showMessage({
        type: 'warning',
        content: 'No records was selected!',
      });
      return;
    }
    const exportColumns = columns.slice(1).map((col) => col.title);
    const exportName = csvConfigExport(
      `Employee&Subcontractor-hold-export`,
      exportColumns as string[],
    );
    const dataExport = selectedRows?.map((sub) => {
      const exportRow = {
        status: formatStatus(sub?.status),
        id: sub?.id || '',
        title: sub?.title || '',
        name: sub?.fullName || '',
        agencyRef: sub?.agencyRef || '',
        holdReason: sub?.holdReason || '',
        paymentCom: sub?.companyPayment?.companyName || '',
        regStatus:
          find(
            defaultRegistrationStatus,
            (item) => item.value === sub?.registrationStatus,
          )?.label || '',
        payFrequency:
          find(
            defaultPaymentFrequency,
            (item) => item.value === sub?.payFrequency,
          )?.label || '',
        ...(!isBureau ? { primaryAgency: sub?.primaryAgency?.name || '' } : {}),
        mobile: sub?.mobile ? `"${sub?.mobile}"` : '',
        email: sub?.email || '',
        NINumber: sub?.NINumber || '',
        UTRNo: sub?.setting?.UTRNo || '',
        cisStatus:
          find(
            defaultCisStatus,
            (item) => item.value === sub?.setting?.cisStatus,
          )?.label || '',
        PVNo: sub?.PVNo || '',
        verificationDate: sub?.setting?.verificationDate
          ? moment(sub?.setting?.verificationDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
        matchDate: sub?.setting?.matchDate
          ? moment(sub?.setting?.matchDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
        marginMethod: sub?.setting?.marginMethod
          ? find(
              defaultMarginMethod,
              (item) => item.value === sub?.setting?.marginMethod,
            )?.label || ''
          : '',
        margin: sub?.setting?.margin || '',
      };
      return exportRow;
    });
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">
            {type === EmployeeModalTypeEnum.ON_HOLD
              ? messages['common.employeesOnHold']
              : type === EmployeeModalTypeEnum.PENSION_OPT_OUT
                ? messages['common.employeesPensionOptOut']
                : messages['common.employees']}
          </div>
          <AppButton name="Export" onClick={handleExport} />
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <div className="flex items-center justify-between gap-2 w-full my-[20px] px-[10px] flex-wrap">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            placeholder="Select Payment Company"
            onChangeValue={(value: any) => {
              setSelectedCompanies(value?.map((item: any) => item.fullData));
            }}
            mode="multiple"
            className="!min-w-[260px]"
            {...(isBureau
              ? {
                  paramFilter: {
                    paymentModalEmployee: PAYMENT_MODEL_ENUM.PAYEE,
                  },
                }
              : {})}
          />
          {!isBureau && (
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder="Select Agency"
              mode="multiple"
              onChangeValue={(value: any) => {
                setSelectedAgencies(value?.map((item: any) => item.fullData));
              }}
              className="!min-w-[165px]"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <AppInputSearch onChange={handleSearch} />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 4000 }}
          columns={columns}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default EmployeeModal;
